import * as React from "react";
import { styled } from "@mui/material";
import { PrimeReactProvider } from "primereact/api";
import { Password, PasswordProps } from "primereact/password";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const StyledPasswordRoot = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "inputError" && prop !== "errorBorderColor",
})<{
  inputError?: boolean;
  errorBorderColor: string;
}>(({ inputError, errorBorderColor }) => ({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  "& > .p-password": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  "& .p-password": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  "& .p-password .p-icon-field": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    position: "relative",
    display: "block",
  },
  "& .p-password.p-input-icon-right .p-icon-field .p-input-icon": {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: 0,
    width: "1.5rem",
    height: "1.5rem",
    zIndex: 1,
  },
  "& .p-password .p-inputtext": {
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: 0,
    borderRadius: "var(--theme-radius-md, 4px)",
    height: "var(--theme-input-height, 37px)",
    /* Align with MUI OutlinedInput + `GomakeTextInput` (~37px row, 14px horizontal inset) */
    padding: "8px 14px",
    fontFamily: "Heebo",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: "21px",
    display: "block",
    color: inputError ? "red" : "#000000",
    boxShadow: "var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
    WebkitBoxShadow:
      "var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
    WebkitAppearance: "none",
    backgroundColor: "var(--theme-bg-primary, #FFF)",
    width: "100%",
    border: "1px solid var(--theme-input-border-color, transparent)",
    borderBottom: inputError
      ? `2px solid ${errorBorderColor}`
      : "0px solid #FFFFFF",
    outline: "none",
    margin: 0,
  },
  "& .p-password.p-input-icon-right .p-inputtext": {
    paddingRight: "2.75rem",
  },
  "& .p-password .p-inputtext:hover": {
    borderColor: "var(--theme-input-border-color, transparent)",
  },
  "& .p-password.p-inputwrapper-focus .p-inputtext": {
    border: `1px solid var(--theme-input-focus-color, var(--theme-input-border-color, transparent))`,
    borderRadius: "var(--theme-radius-md, 4px)",
    boxShadow: "var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
  },
  "& .p-password .p-inputtext::placeholder": {
    color: "#B1B1B1",
    opacity: 1,
    fontFamily: "Heebo",
    fontStyle: "normal",
    fontWeight: 500,
  },
  "& .p-password .p-password-show-icon, & .p-password .p-password-hide-icon": {
    color: "#B1B1B1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    margin: 0,
  },
  "& .p-password .p-password-show-icon svg, & .p-password .p-password-hide-icon svg":
    {
      width: "1.125rem",
      height: "1.125rem",
      display: "block",
    },
  "& .p-password-panel": {
    marginTop: 8,
    padding: "12px 14px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    backgroundColor: "var(--theme-bg-primary, #FFF)",
    borderRadius: "var(--theme-radius-md, 4px)",
    boxShadow: "var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
    fontFamily: "Heebo",
    fontSize: 13,
    lineHeight: 1.45,
    color: "#000000",
    border: "1px solid var(--theme-input-border-color, transparent)",
  },
  "& .p-password-meter": {
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 8,
    backgroundColor: "rgba(0, 0, 0, 0.06)",
  },
  "& .p-password-info": {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 500,
  },
}));

export type GomakePasswordInputProps = Omit<
  PasswordProps,
  "onChange" | "feedback" | "toggleMask" | "invalid"
> & {
  /** Matches `GomakeTextInput` error styling when true. */
  error?: boolean;
  /** Forwarded to Prime `invalid` (e.g. form libraries); combined with `error`. */
  invalid?: boolean;
  /**
   * When true, the field is uncontrolled: do not pass `value` from React state.
   * The password still exists on the DOM input (inspect / `$0.value` can still read it);
   * it will not appear in React DevTools component state.
   */
  uncontrolled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Password strength panel; default false to align with login-style fields. */
  feedback?: boolean;
  /** Show reveal/hide control; default true (see [PrimeReact Password](https://primereact.org/password/)). */
  toggleMask?: boolean;
};

const GomakePasswordInput = React.forwardRef<
  React.ComponentRef<typeof Password>,
  GomakePasswordInputProps
>(function GomakePasswordInput(
  {
    error,
    onChange,
    uncontrolled = false,
    feedback = false,
    toggleMask = true,
    autoComplete = "off",
    invalid,
    value,
    defaultValue,
    inputRef,
    ...rest
  },
  ref,
) {
  const { errorColor } = useGomakeTheme();

  /**
   * PrimeReact `Password` only refreshes the strength meter when `value` changes
   * (`useUpdateEffect` on `props.value`). Uncontrolled usage leaves `value` undefined,
   * so the meter never updates. When `uncontrolled` + `feedback`, keep a local mirror
   * of the field so Prime stays controlled for metering only (still no parent state).
   */
  const needsInternalValueForMeter = uncontrolled && feedback;
  const [internalMeterValue, setInternalMeterValue] = React.useState(() =>
    needsInternalValueForMeter ? String(defaultValue ?? "") : "",
  );

  const changeHandler =
    onChange || needsInternalValueForMeter
      ? (e: React.ChangeEvent<HTMLInputElement>) => {
          const trimmedValue = e.target.value.trimStart();
          if (needsInternalValueForMeter) {
            setInternalMeterValue(trimmedValue);
          }
          if (onChange) {
            const modifiedEvent = {
              ...e,
              target: {
                ...e.target,
                value: trimmedValue,
              },
            };
            onChange(modifiedEvent as React.ChangeEvent<HTMLInputElement>);
          }
        }
      : undefined;

  const showInvalid = Boolean(invalid ?? error);

  const passwordProps = uncontrolled
    ? needsInternalValueForMeter
      ? {
          value: internalMeterValue,
          inputRef,
          ...rest,
          onChange: changeHandler!,
        }
      : {
          defaultValue,
          inputRef,
          ...rest,
          ...(changeHandler ? { onChange: changeHandler } : {}),
        }
    : {
        value,
        defaultValue,
        inputRef,
        ...rest,
        onChange: changeHandler ?? (() => {}),
      };

  return (
    <PrimeReactProvider>
      <StyledPasswordRoot
        className="gomake-password-root"
        inputError={showInvalid}
        errorBorderColor={errorColor(300)}
      >
        <Password
          ref={ref}
          feedback={feedback}
          toggleMask={toggleMask}
          invalid={showInvalid}
          autoComplete={autoComplete}
          {...passwordProps}
        />
      </StyledPasswordRoot>
    </PrimeReactProvider>
  );
});

GomakePasswordInput.displayName = "GomakePasswordInput";

export { GomakePasswordInput };
